import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  plans: [],
  totalPlansCount: 0,
  currentPlan: null,
  loading: false,
  error: null,
};

export const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    setPlans: (state, action) => {
      state.plans = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalPlansCount = action.payload;
    },
    setCurrentPlan: (state, action) => {
      state.currentPlan = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setPlans, setTotalCount, setCurrentPlan, setLoading, setError } = planSlice.actions;

export const getPlans = (filters, silent = false) => async (dispatch) => {
  if (!silent) {
    dispatch(setLoading(true));
  }
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const user_id = filters.user_id || '';
    const type = filters.type || '';
    let url = `/admin/plan/list?page=${page}&limit=${limit}&search=${search}`;
    if (filters.from_date) url += `&from_date=${filters.from_date}`;
    if (filters.to_date) url += `&to_date=${filters.to_date}`;
    if (user_id) url += `&user_id=${user_id}`;
    if (type) url += `&type=${type}`;
    const response = await axiosInstance.get(url);
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const total = response.data.data.total || response.data.data.count || response.data.data.totalCount || result.length;
      dispatch(setPlans(result));
      dispatch(setTotalCount(total));
      return { status: true, data: result, total };
    } else {
      dispatch(setError(response.data?.message || 'Failed to fetch plans'));
      return { status: false, message: response.data?.message || 'Failed to fetch plans' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch plans';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    if (!silent) {
      dispatch(setLoading(false));
    }
  }
};

export const getPlanDetails = (planId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  dispatch(setCurrentPlan(null));
  try {
    const response = await axiosInstance.get(`/admin/plan/getbyId/${planId}`);
    if (response.data && response.data.status === true) {
      dispatch(setCurrentPlan(response.data.data));
      return { status: true, data: response.data.data };
    } else {
      dispatch(setError(response.data?.message || 'Failed to fetch plan details'));
      return { status: false, message: response.data?.message || 'Failed to fetch plan details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch plan details';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Create a new plan
export const createPlan = (payload) => async (dispatch) => {
  try {
    const response = await axiosInstance.post('/admin/plan/create', payload);
    if (response.data && (response.data.status === true || response.data.success === true)) {
      return { status: true, data: response.data.data, message: response.data.message || 'Plan created successfully' };
    } else {
      return { status: false, message: response.data?.message || 'Failed to create plan' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to create plan';
    return { status: false, message: errorMsg };
  }
};

// Update a plan
export const updatePlan = (planId, payload) => async (dispatch) => {
  try {
    const response = await axiosInstance.put(`/admin/plan/update/${planId}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update plan' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update plan';
    return { status: false, message: errorMsg };
  }
};

// Export plans data
export const exportPlans = (filters = {}) => async () => {
  try {
    const params = {};
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.type) params.type = filters.type;
    if (filters.search) params.search = filters.search;

    const response = await axiosInstance.get('/admin/plan/export', {
      params,
      responseType: 'blob',
    });

    if (response.data && response.data.type === 'application/json') {
      const text = await response.data.text();
      const json = JSON.parse(text);
      if (json.status === false) {
        return { status: false, message: json.message || 'Failed to export plans' };
      }
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'plans_export.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { status: true };
  } catch (error) {
    let errorMsg = 'Failed to export plans';
    if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        errorMsg = json.message || errorMsg;
      } catch (e) {
        errorMsg = error.message || errorMsg;
      }
    } else {
      errorMsg = error.response?.data?.message || error.message || errorMsg;
    }
    return { status: false, message: errorMsg };
  }
};

export default planSlice.reducer;

